import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { DataItem } from "../internals/interfaces"
import LoadingData from './LoadingData';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

interface EmissionDataGridProps {
  electricityData: DataItem[];
  gasData: DataItem[];
  loading: boolean;
  aggregation: string;
}

const getRowId = (row: any) => row.reading_time;

const EmissionDataGrid: React.FC<EmissionDataGridProps> = ({ electricityData, gasData, loading, aggregation }) => {  
  const theme = useTheme();
  
  // Process data to combine electricity and gas emissions
  const processData = () => {
    if (electricityData.length === 0 && gasData.length === 0) return [];
    
    // Use electricity data as base for timestamps
    const baseData = electricityData.length > 0 ? electricityData : gasData;
    
    return baseData.map((item: DataItem) => {
      // Find matching gas data
      const gasItem = gasData.find(gas => 
        new Date(gas.reading_time).getTime() === new Date(item.reading_time).getTime()
      );
      
      const electricityEmissions = item.gCO_2 || 0;
      const gasEmissions = gasItem?.gCO_2 || 0;
      const totalEmissions = electricityEmissions + gasEmissions;
      
      return {
        ...item,
        electricity_gCO_2: electricityEmissions,
        gas_gCO_2: gasEmissions,
        total_gCO_2: totalEmissions
      };
    });
  };
  
  // Create theme-aware column definitions
  const getThemeAwareColumns = () => {
    const isDarkMode = theme.palette.mode === 'dark';
    
    // Calculate actual maximum values for each column from the processed data
    const processedData = processData();
    const electricityMax = processedData.length > 0 ? Math.max(...processedData.map(item => item.electricity_gCO_2 || 0)) : 0;
    const gasMax = processedData.length > 0 ? Math.max(...processedData.map(item => item.gas_gCO_2 || 0)) : 0;
    const totalMax = processedData.length > 0 ? Math.max(...processedData.map(item => item.total_gCO_2 || 0)) : 0;
    
    // Helper function to create theme-aware renderCell functions
    const createThemeAwareRenderCell = (colorType: string, min: number, max: number) => {
      return (params: any) => {
        if (params.value === null || params.value === undefined) {
          return <div style={{ width: '100%' }}></div>;
        }
        const value = parseFloat(params.value);
        const backgroundColor = getShade(colorType, min, max, value, isDarkMode);
        return (
          <div style={{ 
            backgroundColor,
            width: '100%', 
          }}>
            {params.formattedValue}
          </div>
        );
      };
    };

    // Define emission columns based on aggregation
    let columns: any[] = [];
    
         if (aggregation === 'none' || aggregation === 'hour') {
       // No aggregation - show time and emissions in grams
       columns = [
         { 
           field: 'reading_time', 
           headerName: 'Time', 
           flex: 1.0, 
           minWidth: 50,
           valueFormatter: (params: any) => new Date(params).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
           renderCell: (params: any) => ( <div style={{ textAlign: 'left', width: '100%' }}>{params.formattedValue}</div>)
         },
         {
           field: 'gCO_2_per_kWh',
           headerName: 'CO₂ intensity (g / kWh)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 100,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(0) : null,
         },
         ...(electricityData.length > 0 ? [{
           field: 'electricity_gCO_2',
           headerName: 'Electricity CO₂ emitted (g)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 120,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(2) : null,
           renderCell: createThemeAwareRenderCell('electricity', 0, electricityMax)
         }] : []),
         ...(gasData.length > 0 ? [{
           field: 'gas_gCO_2',
           headerName: 'Gas CO₂ emitted (g)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 120,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(2) : null,
           renderCell: createThemeAwareRenderCell('gas', 0, gasMax)
         }] : []),
         ...(gasData.length > 0 && electricityData.length > 0 ? [{
           field: 'total_gCO_2',
           headerName: 'Total CO₂ emitted (g)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 120,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(2) : null,
           renderCell: createThemeAwareRenderCell('total', 0, totalMax)
         }] : [])
       ];
    } else {
      // Aggregated data - show period and emissions in kg
      let timeColumn: any;
      switch (aggregation) {
        case 'day':
          timeColumn = {
            field: 'reading_time', 
            headerName: 'Day', 
            flex: 1.0, 
            minWidth: 50,
            valueFormatter: (params: any) => new Date(params).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
            renderCell: (params: any) => ( <div style={{ textAlign: 'left', width: '100%' }}>{params.formattedValue}</div>)
          };
          break;
        case 'week':
          timeColumn = {
            field: 'reading_time', 
            headerName: 'Week', 
            flex: 1.0, 
            minWidth: 50,
            valueFormatter: (params: any) => {
              let dt = new Date(params);
              let to = dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              let from = new Date(dt.setDate(dt.getDate() - 6)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              return from + " - " + to
            },
            renderCell: (params: any) => ( <div style={{ textAlign: 'left', width: '100%' }}>{params.formattedValue}</div>)
          };
          break;
        case 'month':
          timeColumn = {
            field: 'reading_time', 
            headerName: 'Month', 
            flex: 1.0, 
            minWidth: 50,
            valueFormatter: (params: any) => new Date(params).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
            renderCell: (params: any) => ( <div style={{ textAlign: 'left', width: '100%' }}>{params.formattedValue}</div>)
          };
          break;
        case 'year':
          timeColumn = {
            field: 'reading_time', 
            headerName: 'Year', 
            flex: 1.0, 
            minWidth: 50,
            valueFormatter: (params: any) => new Date(params).toLocaleDateString('en-GB', { year: 'numeric' }),
            renderCell: (params: any) => ( <div style={{ textAlign: 'left', width: '100%' }}>{params.formattedValue}</div>)
          };
          break;
        default:
          timeColumn = {
            field: 'reading_time', 
            headerName: 'Time', 
            flex: 1.0, 
            minWidth: 80,
            valueFormatter: (params: any) => new Date(params).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
            renderCell: (params: any) => ( <div style={{ textAlign: 'left', width: '100%' }}>{params.formattedValue}</div>)
          };
      }
      
             columns = [
         timeColumn,
         {
           field: 'gCO_2_per_kWh',
           headerName: 'Avg. CO₂ intensity (kg / kWh)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 80,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(0) : null,
         },
         ...(electricityData.length > 0 ? [{
           field: 'electricity_gCO_2',
           headerName: 'Electricity CO₂ emitted (kg)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 80,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(2) : null,
           renderCell: createThemeAwareRenderCell('electricity', 0, electricityMax)
         }] : []),
         ...(gasData.length > 0 ? [{
           field: 'gas_gCO_2',
           headerName: 'Gas CO₂ emitted (kg)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 80,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(2) : null,
           renderCell: createThemeAwareRenderCell('gas', 0, gasMax)
         }] : []),
         ...(gasData.length > 0 && electricityData.length > 0 ? [{
           field: 'total_gCO_2',
           headerName: 'Total CO₂ emitted (kg)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 80,
           valueFormatter: (x: number) => (x !== null) ? x.toFixed(2) : null,
           renderCell: createThemeAwareRenderCell('total', 0, totalMax)
         }] : [])
       ];
    }

    return columns;
  };

  const columns = getThemeAwareColumns();

  // Helper function to get shade color using alpha channel transparency
  const getShade = (colorType: string, min: number, max: number, value: number, isDarkMode: boolean = false) => {
    // Handle case where max is 0 to avoid division by zero
    if (max === 0) {
      max = 1; // Set to 1 to avoid division by zero, but this will result in no shading
    }
    
    // Calculate alpha based on value relative to maximum
    // Alpha will be 0 (transparent) when value is 0, and 1 (fully opaque) when value equals max
    const alpha = value / max;
    
    if (isDarkMode) {
      // Dark mode: use chart colors with alpha transparency
      if (colorType === 'electricity') {
        // Electricity emissions: dark brown (#67330eff) with alpha
        return `rgba(103, 51, 14, ${alpha})`; // Dark brown with alpha
      } else if (colorType === 'gas') {
        // Gas emissions: golden brown (#b69424ff) with alpha
        return `rgba(182, 148, 36, ${alpha})`; // Golden brown with alpha
      } else {
        // Total emissions: dark brown (#67330eff) with alpha
        return `rgba(103, 51, 14, ${alpha})`; // Dark brown with alpha
      }
    } else {
      // Light mode: use chart colors with alpha transparency
      if (colorType === 'electricity') {
        // Electricity emissions: brown (#714627ff) with alpha
        return `rgba(113, 70, 39, ${alpha})`; // Brown with alpha
      } else if (colorType === 'gas') {
        // Gas emissions: golden brown (#b79833ff) with alpha
        return `rgba(183, 152, 51, ${alpha})`; // Golden brown with alpha
      } else {
        // Total emissions: brown (#714627ff) with alpha
        return `rgba(113, 70, 39, ${alpha})`; // Brown with alpha
      }
    }
  };

  const processedData = processData();

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <LoadingData open={loading} />
      <DataGrid
        sx={{
          // Disable MUI DataGrid's mobile-specific behavior
          "& .MuiDataGrid-root": {
            overflowX: "hidden !important"
          },
          "& .MuiDataGrid-main": {
            overflowX: "hidden !important"
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden !important"
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "normal"
          },
          "& .MuiDataGrid-columnHeader": {
            // Forced to use important since overriding inline styles
            height: "unset !important"
          },
          position: 'relative',
          // Remove border radius on mobile
          [theme.breakpoints.down('md')]: {
            borderRadius: 0,
            '& .MuiDataGrid-main': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-container--top': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-container--bottom': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-columnHeaders': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-footerContainer': {
              borderRadius: 0,
            },
          },
        }}
        autoHeight
        rows={processedData}
        columns={columns}
        rowHeight={40}
        getRowId={getRowId}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }      
        hideFooter  
        disableColumnResize
        density="compact"
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />
    </Box>
  );
}

export default EmissionDataGrid;
